import {
  AreaStorage,
  OrganizationStorage,
  Storage,
  STORAGE_AREAS,
} from '@totem/components/administration/storageReporting/types';
import { formatBytes } from '@totem/utilities/common';

export const formatStorage = (bytes: number) =>
  bytes > 0 ? formatBytes(bytes, 1) : '—';

export const formatPercent = (part: number, whole: number) =>
  whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—';

const sumAreas = (storage: Storage, field: keyof Omit<AreaStorage, 'oldest'>) =>
  STORAGE_AREAS.reduce((total, area) => total + storage[area.key][field], 0);

export const getRecentBytes = (storage: Storage) =>
  sumAreas(storage, 'recentBytes');

export const getDeletedBytes = (storage: Storage) =>
  sumAreas(storage, 'deletedBytes');

export const getOldest = (storage: Storage) =>
  STORAGE_AREAS.map((area) => storage[area.key].oldest)
    .filter((value): value is string => Boolean(value))
    .sort()[0];

export const getNoBuildingBytes = (organization: OrganizationStorage) =>
  organization.buildings
    .filter((building) => !building.buildingId)
    .reduce((total, building) => total + building.totalBytes, 0);

export type StorageFlag = {
  label: string;
  color: 'warning' | 'processing' | 'error' | 'default';
  tooltip: string;
};

const MEGABYTE = 1024 * 1024;

// Flags call out where storage could be reduced or is hard to attribute.
// Small organizations are skipped so the flags stay meaningful.
export const getStorageFlags = (
  organization: OrganizationStorage,
  isDeactivated: boolean,
): StorageFlag[] => {
  const flags: StorageFlag[] = [];
  const { storage, totalBytes } = organization;

  if (!organization.organizationId) {
    flags.push({
      label: 'No organization',
      color: 'error',
      tooltip: 'Records with no organization assigned',
    });
  }

  if (isDeactivated) {
    if (totalBytes > 0) {
      flags.push({
        label: 'Holds data',
        color: 'warning',
        tooltip: 'Deactivated organization still holding records',
      });
    }
    return flags;
  }

  if (totalBytes < MEGABYTE) {
    return flags;
  }

  const noBuildingBytes = getNoBuildingBytes(organization);
  if (organization.organizationId && noBuildingBytes / totalBytes >= 0.25) {
    flags.push({
      label: `${formatPercent(noBuildingBytes, totalBytes)} no building`,
      color: 'warning',
      tooltip: `${formatStorage(noBuildingBytes)} is not assigned to a building`,
    });
  }

  const { devices } = storage;
  if (
    devices.bytes >= MEGABYTE &&
    devices.deletedBytes / devices.bytes >= 0.5
  ) {
    flags.push({
      label: `${formatPercent(devices.deletedBytes, devices.bytes)} devices deleted`,
      color: 'warning',
      tooltip: `${formatStorage(devices.deletedBytes)} of device records are soft-deleted`,
    });
  }

  const eventCount = storage.events.count + storage.eventsCleared.count;
  const recentEvents =
    storage.events.recentCount + storage.eventsCleared.recentCount;
  if (eventCount > 0 && recentEvents === 0) {
    flags.push({
      label: 'No new events',
      color: 'default',
      tooltip: 'Holds events but none were created in the last 30 days',
    });
  }

  return flags;
};
